import { mailtoBugReport } from "util";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import PropTypes from "prop-types";
import { useEffect, Component } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Dropdown from "react-bootstrap/Dropdown";
import DropdownButton from "react-bootstrap/DropdownButton";
import ReactGA from "react-ga4";
import { connect, useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import empty from "assets/empty.png";
import Spinner from "components/Spinner";
import { addCards } from "store/cards";
import {
  buyAtCardKingdom,
  buyAtTCGPlayer,
  clearClipboard,
  closeMobileImage,
  copyClipboard,
  exportToArchidekt,
  exportToMoxfield,
  getPrices,
  toggleClipboard,
  fetchRecs,
  getOrganizedCards,
  getAllCards,
  undo,
} from "store/clipboard";

import styles from "./Clipboard.module.scss";
import ClipboardItems from "./ClipboardItems";

const Clipboard = () => {
  const dispatch = useDispatch();
  const cards = useSelector((state) => state.cards.cards);
  const clipboard = useSelector((state) => state.clipboard);
  const isLoadingRecs = useSelector((state) => state.clipboard.isLoadingRecs);
  const isOpen = useSelector((state) => state.clipboard.isOpen);
  const mobileImageIsOpen = useSelector((state) => state.clipboard.mobileImageIsOpen);
  const recs = useSelector((state) => state.clipboard.recs);
  const viewerImage = useSelector((state) => state.clipboard.viewerImage);

  useEffect(async () => {
    const allCards = Object.keys(dispatch(getAllCards()));
    if (allCards.length > 0) {
      dispatch(addCards(allCards));
    }
  }, []);

  const commander =
    cards[clipboard.commander] && !cards[clipboard.commander].loading ? cards[clipboard.commander] : null;
  const organizedLists = dispatch(getOrganizedCards());
  const prices = dispatch(getPrices());

  const isEmpty = Object.keys(dispatch(getAllCards()))?.length === 0;

  const handleRecs = () => dispatch(fetchRecs());

  return (
    <div className={classNames(styles.clipboard, { [styles.open]: isOpen }, "edhrec-clipboard shadow")}>
      <div className={styles.toggle} onClick={() => dispatch(toggleClipboard())}>
        <div className={styles.clipboardToggleArrowMobile}>
          <FontAwesomeIcon icon="paperclip" /> {Object.keys(clipboard.cards)?.length + (clipboard.commander ? 1 : 0)}
        </div>
      </div>
      <div className="d-flex h-100 w-100">
        <div className="d-flex flex-column justify-content-center w-100">
          <>
            <div className={classNames(styles.cards, styles.clipboardBorder)}>
              <ClipboardItems commander={commander} lists={organizedLists} recs={recs} />
              {isLoadingRecs && (
                <>
                  <div className="w-100">Recs</div>
                  <div className={styles.recsLoader}>
                    <div />
                    <div />
                    <div />
                  </div>
                </>
              )}
            </div>
            <div className={styles.buttons}>
              <div className={classNames(styles.buttonRow, "d-none d-sm-flex")}>
                <Button disabled={isEmpty} onClick={() => dispatch(clearClipboard())} variant="danger">
                  <FontAwesomeIcon icon="trash" />
                </Button>
                <Button
                  disabled={clipboard.prevStates?.length === 0}
                  onClick={() => dispatch(undo())}
                  variant="secondary"
                >
                  <FontAwesomeIcon icon="rotate-left" /> Undo
                </Button>
              </div>
              <div className={styles.buttonRow}>
                <DropdownButton title={<span>{" Export"}</span>} variant="secondary">
                  <Dropdown.Item onClick={() => dispatch(exportToArchidekt())} variant="secondary">
                    Archidekt
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => dispatch(exportToMoxfield())} variant="secondary">
                    Moxfield
                  </Dropdown.Item>
                  <Dropdown.Item onClick={() => dispatch(copyClipboard())} variant="secondary">
                    Clipboard
                  </Dropdown.Item>
                </DropdownButton>
                <Button onClick={() => dispatch(buyAtCardKingdom())} variant="secondary">
                  Card Kingdom: ${prices.cardkingdom.toFixed(2)}
                </Button>
                <Button onClick={() => dispatch(buyAtTCGPlayer())} variant="secondary">
                  TCGplayer: ${prices.tcgplayer.toFixed(2)}
                </Button>
                <Button
                  className="d-none d-sm-block"
                  disabled={isLoadingRecs}
                  onClick={handleRecs}
                  variant={commander ? "primary" : "secondary"}
                >
                  {isLoadingRecs ? <Spinner /> : "Recs"}
                </Button>
              </div>
              <div className={classNames(styles.mobileButtons)}>
                <Button disabled={isEmpty} onClick={() => dispatch(clearClipboard())} variant="danger">
                  <FontAwesomeIcon icon="trash" />
                </Button>
                <Button
                  disabled={clipboard.prevStates?.length === 0}
                  onClick={() => dispatch(undo())}
                  variant="secondary"
                >
                  <FontAwesomeIcon icon="rotate-left" /> Undo
                </Button>
                <Button
                  className={styles.big}
                  disabled={isLoadingRecs}
                  onClick={handleRecs}
                  variant={commander ? "primary" : "secondary"}
                >
                  {isLoadingRecs ? <Spinner /> : "Recs"}
                </Button>
              </div>
              {clipboard.error && (
                <Alert className="d-block d-sm-none" variant="danger">
                  {clipboard.error}
                </Alert>
              )}
            </div>
          </>
        </div>
        <div className={styles.imageWrapper}>
          <div className={styles.imageViewer}>
            <img
              alt="Preview"
              className={classNames(styles.image, {
                empty: viewerImage === "",
              })}
              src={viewerImage || empty}
            />
          </div>
          {clipboard.error && (
            <Alert className={styles.error} variant="danger">
              {clipboard.error}
            </Alert>
          )}
        </div>
      </div>
      <div
        className={classNames(styles.mobileImageViewer, {
          [styles.open]: isOpen && mobileImageIsOpen,
        })}
        onClick={() => dispatch(closeMobileImage())}
      >
        <div className={styles.mobileImage}>
          <img alt="Preview" src={viewerImage || empty} />
        </div>
      </div>
    </div>
  );
};
class ClipboardErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static propTypes = {
    clearClipboard: PropTypes.func.isRequired,
    isOpen: PropTypes.bool.isRequired,
  };

  static getDerivedStateFromError(error) {
    return { error, hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    ReactGA.event({ action: error.message, category: "Errors", label: "Clipboard" });
    this.props.clearClipboard();
  }

  render() {
    const { error } = this.state;
    return (
      <div className={classNames(styles.clipboard, { [styles.open]: this.props.isOpen }, "edhrec-clipboard shadow")}>
        {this.state.hasError ? (
          <Alert className="m-4" variant="danger">
            <h4>An error occurred</h4>
            <p className="mb-2">Something went wrong, and our team has been automatically notified.</p>
            <code>{error.message}</code>
            <p className="mb-0 mt-2">
              You can <Link to="/bug-report">submit a bug report</Link>, or email us at{" "}
              <Link target="_blank" to={mailtoBugReport(error.stack || error.message)}>
                bugreport@edhrec.com
              </Link>
              .
            </p>
          </Alert>
        ) : (
          <Clipboard />
        )}
      </div>
    );
  }
}

export default connect((state) => ({ isOpen: state.clipboard.isOpen }), { clearClipboard })(ClipboardErrorBoundary);
